/**
 * Type-based vector graphics for fleet cards.
 * The booking API exposes NO per-boat photography, so we render an honest
 * illustration keyed off assetTypeName rather than faking a photo.
 */
const Hull = ({ children, label }) => (
  <svg
    viewBox="0 0 320 180"
    role="img"
    aria-label={label}
    className="h-full w-full"
    preserveAspectRatio="xMidYMid meet"
  >
    <defs>
      <linearGradient id="wbWater" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="#1B7B86" stopOpacity="0.16" />
        <stop offset="100%" stopColor="#0F2A3D" stopOpacity="0.28" />
      </linearGradient>
    </defs>
    <rect width="320" height="180" fill="#F2EFE8" />
    <path d="M0 132 Q80 118 160 132 T320 132 V180 H0Z" fill="url(#wbWater)" />
    <g stroke="#0F2A3D" strokeWidth="3.2" strokeLinecap="round" strokeLinejoin="round" fill="none">
      {children}
    </g>
    <path d="M14 152 q22 -8 44 0 t44 0 t44 0 t44 0 t44 0 t44 0" stroke="#1B7B86" strokeWidth="2.4" fill="none" opacity="0.6" />
  </svg>
);

const GRAPHICS = {
  pontoon: (label) => (
    <Hull label={label}>
      <path d="M52 124 h216 l-14 16 H66Z" fill="#16374E" />
      <path d="M66 124 v-26 h140 v26" />
      <path d="M70 98 h132" stroke="#D96B27" />
      <path d="M96 98 v-30 h84 v30" />
      <path d="M92 68 h92" stroke="#D96B27" strokeWidth="4" />
      <path d="M206 124 v-14 h44" />
      <circle cx="252" cy="112" r="5" fill="#D96B27" stroke="none" />
    </Hull>
  ),
  tritoon: (label) => (
    <Hull label={label}>
      <path d="M44 126 h232 l-18 16 H60Z" fill="#16374E" />
      <path d="M60 126 v-28 h150 v28" />
      <path d="M64 98 h142" stroke="#D96B27" />
      <path d="M92 98 v-32 h74 l14 32" />
      <path d="M210 126 v-18 h52" />
      <path d="M266 92 l22 -10 M266 104 l26 -2 M266 116 l22 8" stroke="#1B7B86" strokeWidth="4" />
      <circle cx="258" cy="110" r="5" fill="#D96B27" stroke="none" />
    </Hull>
  ),
  "center console": (label) => (
    <Hull label={label}>
      <path d="M40 122 q34 22 128 22 t112 -22 z" fill="#16374E" />
      <path d="M40 122 q34 -34 128 -34 t112 34" />
      <path d="M140 88 v-30 h46 v30" />
      <path d="M136 58 h54" stroke="#D96B27" strokeWidth="4" />
      <path d="M186 58 v-30" stroke="#1B7B86" />
      <path d="M60 110 h84" stroke="#D96B27" />
    </Hull>
  ),
  "jet ski": (label) => (
    <Hull label={label}>
      <path d="M70 124 q40 20 110 18 t68 -30 q-40 -16 -96 -12 t-82 24z" fill="#16374E" />
      <path d="M120 112 q22 -26 56 -22" />
      <path d="M176 90 l26 6" stroke="#D96B27" strokeWidth="5" />
      <path d="M248 112 l32 -8 M248 122 l34 4" stroke="#1B7B86" strokeWidth="4" />
    </Hull>
  ),
  kayak: (label) => (
    <Hull label={label}>
      <path d="M40 126 q120 26 240 0 q-120 -20 -240 0z" fill="#16374E" />
      <path d="M150 118 v-24 h22 v24" />
      <path d="M96 84 l128 40" stroke="#D96B27" strokeWidth="5" />
    </Hull>
  ),
  default: (label) => (
    <Hull label={label}>
      <circle cx="160" cy="84" r="34" />
      <path d="M160 50 v68 M126 84 h68" stroke="#D96B27" />
      <path d="M160 118 q-26 -8 -26 -34 M160 118 q26 -8 26 -34" stroke="#1B7B86" />
    </Hull>
  ),
};

export const BoatTypeGraphic = ({ typeName }) => {
  const key = String(typeName || "").toLowerCase();
  const match =
    Object.keys(GRAPHICS).find((k) => k !== "default" && key.includes(k)) ||
    (key.includes("waverunner") ? "jet ski" : null) ||
    "default";
  const label = typeName ? `${typeName} illustration` : "Marine badge illustration";
  return (
    <div
      data-testid="boat-type-graphic"
      className="relative w-full overflow-hidden bg-sand-card"
      style={{ aspectRatio: "16 / 9" }}
    >
      {GRAPHICS[match](label)}
    </div>
  );
};

export default BoatTypeGraphic;
